// react
import { useState } from "react";
// kendo
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { process, State } from "@progress/kendo-data-query";
// api
import { GetPublicTitlesListItemDto } from "../../../../api/Client";
// common
import TitleLink from "../../../../common/links/TitleLinkView";
import * as GlobalHelpers from "../../../../common/GlobalHelpers";
import CustomGridCell from "../../../../common/kendoGrid/CustomGridCell";
import { createGridColumnFilterMenu } from "../../../../common/kendoGrid/GridColumnFilterMenu";
import { createGridColumnCheckboxFilterMenu } from "../../../../common/kendoGrid/GridColumnCheckboxFilterMenu";

const initialGridState: State = {
    sort: [
        { field: "lastRenewalDate", dir: "desc" },
        { field: "initialGrantDate", dir: "desc" },
    ],
    skip: 0,
    take: 10,
};

interface IProps {
    filter: string;
    titles: GetPublicTitlesListItemDto[];
    onSearch: (value: string) => void;
}

export default function ListView(props: IProps) {
    const { filter, titles, onSearch } = props;

    const [gridState, setGridState] = useState(initialGridState);
    const [searchText, setSearchText] = useState(filter);

    function handleSearchTextChanged(e: React.ChangeEvent<HTMLInputElement>) {
        setSearchText(e.target.value);
    }

    function handleSearchClicked() {
        onSearch(searchText);
        setGridState(initialGridState);
    }

    function handleSearchTextKeyUp(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key !== "Enter") return;
        handleSearchClicked();
    }

    function handleResetSearchClicked() {
        setSearchText("");
        onSearch("");
        setGridState(initialGridState);
    }

    return (
        <>
            {/* search box */}
            <div className="d-flex align-items-center mb-3">
                <label htmlFor="searchText" className="visually-hidden">
                    Search
                </label>
                <input
                    id="searchText"
                    className="form-control me-2"
                    type="text"
                    placeholder="Search"
                    style={{ width: "22rem" }}
                    value={searchText}
                    onChange={handleSearchTextChanged}
                    onKeyUp={handleSearchTextKeyUp}
                />
                <button id="searchTextButton" className="btn btn-outline-primary me-1" type="button" onClick={handleSearchClicked}>
                    Search
                </button>
                <button id="resetSearchTextButton" className="btn btn-outline-secondary" type="button" onClick={handleResetSearchClicked}>
                    Reset
                </button>
            </div>

            {/* results grid */}
            <Grid
                pageable
                sortable
                resizable
                navigatable
                data={process(titles, gridState)}
                {...gridState}
                onDataStateChange={(e) => {
                    setGridState(e.dataState);
                }}
            >
                <GridColumn
                    field="titleNumber"
                    title="Title"
                    width={160}
                    filter="text"
                    columnMenu={(p) => createGridColumnCheckboxFilterMenu(p, titles)}
                    cell={(props) => (
                        <CustomGridCell gridCellProps={props}>
                            <TitleLink titleId={props.dataItem.titleId} titleNumber={props.dataItem.titleNumber} />
                        </CustomGridCell>
                    )}
                />
                <GridColumn field="titleType" title="Title Type" width={220} filter="text" columnMenu={(p) => createGridColumnCheckboxFilterMenu(p, titles)} />
                <GridColumn field="titleStatus" title="Status" width={160} filter="text" columnMenu={(p) => createGridColumnCheckboxFilterMenu(p, titles)} />
                <GridColumn field="titleholders" title="Titleholders" width={500} filter="text" columnMenu={createGridColumnFilterMenu} />
                <GridColumn field="currentExpiryDate" title="Expiry" format={GlobalHelpers.NoptaDateFormatForKendo} width={160} filter="date" columnMenu={createGridColumnFilterMenu} />
                <GridColumn field="lastRenewalDate" title="Renewal" format={GlobalHelpers.NoptaDateFormatForKendo} width={170} filter="date" columnMenu={createGridColumnFilterMenu} />
                <GridColumn field="initialGrantDate" title="Grant" format={GlobalHelpers.NoptaDateFormatForKendo} width={160} filter="date" columnMenu={createGridColumnFilterMenu} />
                <GridColumn field="offshoreAreaName" title="Offshore Area" width={285} filter="text" columnMenu={(p) => createGridColumnCheckboxFilterMenu(p, titles)} />
                <GridColumn field="basinName" title="Basin" width={200} filter="text" columnMenu={(p) => createGridColumnCheckboxFilterMenu(p, titles)} />
            </Grid>
        </>
    );
}
